import {Trans} from '@lingui/macro';
import _, {last} from 'lodash';
import {DateTime} from 'luxon';
import React from 'react';

import {reportToBugsnag} from '../../srcCommon/services/bugsnag/bugsnag';
import {build12Or24hOptionsForFormat, TimeFormatsEnum} from '../helpers/dateTimeHelpers';
import {
  CalendarEventVersionReminder,
  CalendarReminderTypesEnum,
} from '../models/api/calendarEventVersionModel';
import {
  ApiCalendarNotificationLinkParameters,
  serializeCalendarNotificationLinkParameters,
} from '../models/api/calendarNotificationLinkModel';
import {CalendarEventParameters} from '../store/panels/calendars/calendarsPanelState';
import {readApiModel} from './apiHelpers';

export enum CalendarReminderUnitsEnum {
  MINUTE = 'minute',
  HOUR = 'hour',
  DAY = 'day',
  WEEK = 'week',
}

export interface CalendarReminderValue {
  unit: CalendarReminderUnitsEnum;
  count: number;
}

const calendarReminderUnitToMinutes = {
  [CalendarReminderUnitsEnum.MINUTE]: 1,
  [CalendarReminderUnitsEnum.HOUR]: 60,
  [CalendarReminderUnitsEnum.DAY]: 60 * 24,
  [CalendarReminderUnitsEnum.WEEK]: 60 * 24 * 7,
};

export function buildReminderValueForCalendarEventVersionReminder(reminder: CalendarEventVersionReminder) {
  const {minutesBefore} = reminder;

  // Handle the 0 minutes before edge case.
  if (minutesBefore === 0) {
    return {
      count: 0,
      unit: CalendarReminderUnitsEnum.MINUTE,
    };
  }

  const descendingUnits = [
    CalendarReminderUnitsEnum.WEEK,
    CalendarReminderUnitsEnum.DAY,
    CalendarReminderUnitsEnum.HOUR,
  ];
  const reminderValueUnit = descendingUnits.find(
    (unit) => minutesBefore % calendarReminderUnitToMinutes[unit] === 0,
  );

  return {
    count: reminderValueUnit
      ? minutesBefore / calendarReminderUnitToMinutes[reminderValueUnit]
      : minutesBefore,
    unit: reminderValueUnit || CalendarReminderUnitsEnum.MINUTE,
  };
}

export function buildCalendarEventVersionRemindersForValues(values: ReadonlyArray<CalendarReminderValue>) {
  return _(values)
    .map((v) => ({
      type: CalendarReminderTypesEnum.POPUP,
      minutesBefore: getMinutesBefore(v),
    }))
    .filter(
      (r) =>
        r.minutesBefore >= calendarReminderUnitToMinMaxValues[CalendarReminderUnitsEnum.MINUTE].min &&
        r.minutesBefore <= calendarReminderUnitToMinMaxValues[CalendarReminderUnitsEnum.MINUTE].max,
    )
    .uniqBy((r) => r.minutesBefore)
    .value();
}

function getMinutesBefore(value: CalendarReminderValue) {
  const {count, unit} = value;
  return calendarReminderUnitToMinutes[unit] * count;
}

// We currently support reminders up to 4 weeks before the meeting starts.
export const calendarReminderUnitToMinMaxValues = {
  [CalendarReminderUnitsEnum.MINUTE]: {
    min: 0,
    max: 40320,
  },
  [CalendarReminderUnitsEnum.HOUR]: {
    min: 0,
    max: 672,
  },
  [CalendarReminderUnitsEnum.DAY]: {
    min: 0,
    max: 28,
  },
  [CalendarReminderUnitsEnum.WEEK]: {
    min: 0,
    max: 4,
  },
};

export const calendarReminderUnitToText = {
  [CalendarReminderUnitsEnum.WEEK]: <Trans id="calendar-reminder-unit-weeks">weeks</Trans>,
  [CalendarReminderUnitsEnum.DAY]: <Trans id="calendar-reminder-unit-days">days</Trans>,
  [CalendarReminderUnitsEnum.HOUR]: <Trans id="calendar-reminder-unit-hours">hours</Trans>,
  [CalendarReminderUnitsEnum.MINUTE]: <Trans id="calendar-reminder-unit-minutes">minutes</Trans>,
};

/**
 * Builds the calendar event time text to be shown in a reminder notification:
 * All day: "Sept 8" or "Sept 8 - Sept 9"
 * Standard: 11:00 AM - 11:30 PM
 */
export function buildCalendarReminderTimeText(
  startDateTime: DateTime,
  endDateTime: DateTime,
  isAllDay: boolean,
  timeFormat: TimeFormatsEnum,
) {
  const allDayFormat: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  };

  const standardFormat: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
    ...build12Or24hOptionsForFormat(timeFormat),
  };

  const difference = endDateTime.diff(startDateTime, 'days');
  if (isAllDay && difference.as('days') <= 1) {
    return startDateTime.toLocaleString(allDayFormat);
  }

  if (isAllDay) {
    return `${startDateTime.toLocaleString(allDayFormat)} - ${endDateTime
      .minus({days: 1})
      .toLocaleString(allDayFormat)}`;
  }

  return `${startDateTime.toLocaleString(standardFormat)} - ${endDateTime.toLocaleString(standardFormat)}`;
}

export const calendarNotificationLinkPrefix = '/open-calendar-event/';
export function buildCalendarNotificationLink(
  calendarEventParameters: CalendarEventParameters,
  focusedDate: number,
) {
  const payload = serializeCalendarNotificationLinkParameters({
    focusedDate,
    calendarEventParameters,
  });

  // Stringify the payload and then base64 encode for the URL.
  return `${calendarNotificationLinkPrefix}${btoa(JSON.stringify(payload))}`;
}

export function tryParseCalendarNotificationLink(link: string) {
  if (!link.startsWith(calendarNotificationLinkPrefix)) {
    return null;
  }

  const encodedPayload = last(link.split('/'));
  if (!encodedPayload) {
    return null;
  }

  try {
    // Base64 decode and parse the stringified JSON.
    const src = JSON.parse(atob(encodedPayload));
    return readApiModel(ApiCalendarNotificationLinkParameters, src);
  } catch (e) {
    reportToBugsnag(new Error(`Failed to parse calendar notification link: ${link}`));
    return null;
  }
}
